/**
 * Recurring Tickets — form submit routing.
 *
 * Accepts the same `generatedData` object that normal ticket create produces
 * (from `getGeneratedData` in CreateTicketDrawer), adds the recurring-specific
 * fields on top, and calls the recurring ticket API.
 *
 * This ensures the recurring ticket payload is always identical to the normal
 * ticket payload + frequency fields — no divergence.
 */

import toast from "react-hot-toast";

import {
  useCreateRecurringTicket,
  useGlobalStore,
  useUpdateRecurringTicket,
} from "controller";
import { dayjs } from "utilz";

/** Ensure creation_time is always 24h "HH:mm" regardless of input format. */
const to24h = (time?: string): string => {
  if (!time) return "";
  // Already 24h (e.g. "15:05") — dayjs parses "HH:mm" natively.
  // 12h (e.g. "03:05 PM") — dayjs parses "hh:mm A".
  const parsed = dayjs(`2000-01-01 ${time}`, ["YYYY-MM-DD HH:mm", "YYYY-MM-DD hh:mm A"]);
  return parsed.isValid() ? parsed.format("HH:mm") : time;
};
import type {
  CreateRecurringTicketPayload,
  CreateRecurringTicketResponse,
  RecurringTicketFrequency,
  UpdateRecurringTicketPayload,
  UpdateRecurringTicketResponse,
} from "shared";

export interface RecurringFormSubmitOptions {
  editMappingId?: string;
  recurringFields: {
    frequency?: string;
    creation_time?: string;
    start_date?: string;
    end_date?: string | null;
  };
  dirtyFields?: Record<string, boolean>;
  /**
   * Entry point the recurring ticket is being created from. Forwarded to the
   * backend as `created_from` so it can branch on immediate-ticket creation.
   * Only used on create (ignored on update).
   */
  createdFrom?: "recurring_tickets" | "ticket_drawer";
}

export interface RecurringFormSubmitApi {
  submitRecurring: (
    generatedData: Record<string, unknown>,
    opts: RecurringFormSubmitOptions
  ) => Promise<CreateRecurringTicketResponse | UpdateRecurringTicketResponse>;
  isLoading: boolean;
}

/**
 * Maps the normal ticket `generatedData` object to `CreateRecurringTicketPayload`.
 * The field names differ (frontend vs backend convention), so we map them here.
 */
const mapToCreatePayload = (
  data: Record<string, unknown>,
  recurringFields: RecurringFormSubmitOptions["recurringFields"],
  companyTimezone: string,
  createdFrom: "recurring_tickets" | "ticket_drawer",
): CreateRecurringTicketPayload => {
  const payload: CreateRecurringTicketPayload = {
    // Required recurring fields
    frequency: (recurringFields.frequency || "weekly") as RecurringTicketFrequency,
    creation_time: to24h(recurringFields.creation_time) || "09:00",
    // Form already holds end-of-day MSP-tz UTC ISO (snapped at the DateInput
    // change handler in RecurringTicketFormSection).
    start_date: recurringFields.start_date || "",
    time_zone: companyTimezone || "UTC",
    // Map from getGeneratedData's field names to backend field names
    ticket_summary: data.summary as string,
    customer_id: data.customer as string,
    customer_user_id: data.user as string,
    created_from: createdFrom,
  };

  // Pass through everything from getGeneratedData, mapped to backend names
  if (data.description) payload.ticket_description = data.description as string;
  if (data.status) payload.ticket_status = data.status as string;
  if (data.priority) payload.priority = data.priority as CreateRecurringTicketPayload["priority"];
  if (data.urgency) payload.urgency = data.urgency as CreateRecurringTicketPayload["urgency"];
  if (typeof data.isBillable === "boolean") payload.is_billable = data.isBillable;
  if (data.budgetedHours) payload.budgeted_hours = data.budgetedHours as string;
  if (data.board) payload.board_id = data.board as string;
  if (data.ticketType) payload.ticket_type_id = data.ticketType as string;
  if (data.ticketSubtype) payload.ticket_subtype_id = data.ticketSubtype as string;
  if (data.site) payload.site_id = data.site as string;
  if (data.ticket_site_id) payload.ticket_site_id = data.ticket_site_id as string;
  if (data.sla) payload.sla_id = data.sla as string;
  if (data.contract) payload.contract_id = data.contract as string;
  if (data.impact) payload.impact = data.impact as string;
  if (data.estimatedEndTime) payload.estimated_end_date = data.estimatedEndTime as string;
  if (recurringFields.end_date) {
    payload.end_date = recurringFields.end_date;
  }

  // Pass through arrays exactly as getGeneratedData produces them
  if (data.tags) payload.tags = data.tags as unknown[];
  if (data.schedule) payload.schedulings = data.schedule as CreateRecurringTicketPayload["schedulings"];
  if (data.checklist) payload.checklists = data.checklist as CreateRecurringTicketPayload["checklists"];
  if (data.attachments) payload.attachments = data.attachments as unknown[];
  if (Array.isArray(data.ccMembers) && data.ccMembers.length > 0) {
    payload.cc_members = data.ccMembers as CreateRecurringTicketPayload["cc_members"];
  }

  return payload;
};

/**
 * Builds a PATCH payload from the generatedData + dirty fields.
 */
const mapToUpdatePayload = (
  data: Record<string, unknown>,
  recurringFields: RecurringFormSubmitOptions["recurringFields"],
  dirty: Record<string, boolean>,
): UpdateRecurringTicketPayload => {
  const patch: UpdateRecurringTicketPayload = {};

  if (dirty.summary) patch.ticket_summary = data.summary as string;
  if (dirty.description) patch.ticket_description = data.description as string | undefined;
  if (dirty.status) patch.ticket_status = data.status as string;
  if (dirty.priority) patch.priority = data.priority as UpdateRecurringTicketPayload["priority"];
  if (dirty.urgency) patch.urgency = data.urgency as UpdateRecurringTicketPayload["urgency"];
  if (dirty.isBillable) patch.is_billable = data.isBillable as boolean;
  if (dirty.budgetedHours) patch.budgeted_hours = data.budgetedHours as string;
  if (dirty.board) patch.board_id = data.board as string;
  if (dirty.ticketType) patch.ticket_type_id = data.ticketType as string;
  if (dirty.ticketSubType) patch.ticket_subtype_id = data.ticketSubtype as string;
  if (dirty.sla) patch.sla_id = data.sla as string | undefined;
  if (dirty.contract) patch.contract_id = data.contract as string | undefined;
  if (dirty.impact) patch.impact = data.impact as string | undefined;
  if (dirty.estimatedDateAndTime) patch.estimated_end_date = data.estimatedEndTime as string | null;
  if (dirty.tags) patch.tags = data.tags as unknown[];
  if (data.schedule) patch.schedulings = data.schedule as UpdateRecurringTicketPayload["schedulings"];
  if (data.checklist) patch.checklists = data.checklist as UpdateRecurringTicketPayload["checklists"];
  if (data.attachments) patch.attachments = data.attachments as unknown[];
  if (dirty.ccMembers) {
    patch.cc_members = Array.isArray(data.ccMembers)
      ? (data.ccMembers as UpdateRecurringTicketPayload["cc_members"])
      : [];
  }

  // Recurring-specific fields
  if (dirty.recurring_frequency) patch.frequency = recurringFields.frequency as RecurringTicketFrequency;
  if (dirty.recurring_creation_time) patch.creation_time = to24h(recurringFields.creation_time);
  // Form already holds end-of-day MSP-tz UTC ISO (snapped at the DateInput).
  if (dirty.recurring_start_date) {
    patch.start_date = recurringFields.start_date || "";
  }
  if (dirty.recurring_end_date) {
    patch.end_date = recurringFields.end_date || null;
  }

  return patch;
};

export const useRecurringTicketFormSubmit = (): RecurringFormSubmitApi => {
  // MSP timezone (the company's tz from settings) — used for recurring
  // ticket payloads. Distinct from `state.timezone` which is the resource's
  // own viewing timezone.
  const companyTimezone = useGlobalStore((state) => state.mspTimezone);
  const { mutateAsync: createRecurringTicket, isLoading: isCreating } =
    useCreateRecurringTicket();
  const { mutateAsync: updateRecurringTicket, isLoading: isUpdating } =
    useUpdateRecurringTicket();

  const submitRecurring = async (
    generatedData: Record<string, unknown>,
    opts: RecurringFormSubmitOptions,
  ) => {
    if (opts.editMappingId) {
      const dirty = opts.dirtyFields ?? {};
      const patch = mapToUpdatePayload(
        generatedData,
        opts.recurringFields,
        dirty,
      );
      if (Object.keys(patch).length === 0) {
        toast.error("No changes to save");
        throw new Error("No changes to save");
      }
      const response = await updateRecurringTicket({
        mappingId: opts.editMappingId,
        payload: patch,
      });
      toast.success("Recurring ticket updated");
      return response;
    }

    const payload = mapToCreatePayload(
      generatedData,
      opts.recurringFields,
      companyTimezone,
      opts.createdFrom ?? "ticket_drawer",
    );
    const response = await createRecurringTicket(payload);
    toast.success("Recurring ticket created");
    return response;
  };

  return {
    submitRecurring,
    isLoading: isCreating || isUpdating,
  };
};
